/* ==========================================================================
   DH Product Deep Dive — Card carousel behavior

   Desktop (1200px+): Transform-based carousel. No native scroll.
   Cards slide via translateX based on active index. Dots control selection.
   Active card is always in position 1 (leftmost visible).

   Mobile/Tablet (<1200px): Native scroll with snap. IntersectionObserver
   tracks the centered card for .is-active state.
   ========================================================================== */

if (!customElements.get('dh-product-deep-dive')) {
  class DhProductDeepDive extends HTMLElement {
    connectedCallback() {
      this.mq = window.matchMedia('(min-width: 1200px)');
      this.grid = this.querySelector('[data-dh-dd-card-grid]');
      if (!this.grid) return;

      this.cards = Array.from(this.grid.querySelectorAll('[data-dh-dd-card]'));
      if (!this.cards.length) return;

      this.dots = Array.from(this.querySelectorAll('[data-dh-dd-dot]'));
      this.arrowPrev = this.querySelector('[data-dh-dd-arrow="prev"]');
      this.arrowNext = this.querySelector('[data-dh-dd-arrow="next"]');

      this._observer = null;
      this._dotHandlers = null;
      this._activeCard = null;
      this._activeIndex = 0;
      this._activeDotIndex = -1;
      this._isFlipped = this.hasAttribute('data-layout-flip');

      this._onBreakpointChange = this._init.bind(this);
      this._onSectionLoad = this._handleEditorEvent.bind(this);
      this._onSectionSelect = this._handleEditorEvent.bind(this);

      if (this.mq.addEventListener) {
        this.mq.addEventListener('change', this._onBreakpointChange);
      } else {
        this.mq.addListener(this._onBreakpointChange);
      }

      document.addEventListener('shopify:section:load', this._onSectionLoad);
      document.addEventListener('shopify:section:select', this._onSectionSelect);

      this._init();
    }

    disconnectedCallback() {
      this._teardown();

      if (this.mq) {
        if (this.mq.removeEventListener) {
          this.mq.removeEventListener('change', this._onBreakpointChange);
        } else {
          this.mq.removeListener(this._onBreakpointChange);
        }
      }

      document.removeEventListener('shopify:section:load', this._onSectionLoad);
      document.removeEventListener('shopify:section:select', this._onSectionSelect);
    }

    _handleEditorEvent(e) {
      var container = e && e.target ? e.target : document;
      if (!container.contains || !container.contains(this)) return;
      this._init();
    }

    _teardown() {
      if (this._observer) {
        try { this._observer.disconnect(); } catch (e) {}
        this._observer = null;
      }
      if (this._onScroll && this.grid) {
        this.grid.removeEventListener('scroll', this._onScroll);
        this._onScroll = null;
      }
      if (this._scrollTimer) {
        cancelAnimationFrame(this._scrollTimer);
        this._scrollTimer = null;
      }
      if (this._videoDebounce) {
        clearTimeout(this._videoDebounce);
        this._videoDebounce = null;
      }
      this._teardownDots();
      this._teardownArrows();
      this._activeCard = null;
      this._activeIndex = 0;
      this._activeDotIndex = -1;
      // Reset transform
      if (this.grid) {
        this.grid.style.transform = '';
      }
    }

    _init() {
      if (!this.grid || !this.cards.length) return;
      this._teardown();
      this._setupDots();
      this._setupArrows();

      if (this.mq.matches) {
        // Desktop — transform-based carousel
        this._setupDesktop();
      } else {
        // Mobile/tablet — native scroll
        this._setupMobileScroll();
      }

      this._setActive(0);
    }

    // ---- Video helpers ----

    _getCardVideo(card) {
      return card ? card.querySelector('video.dh-dd__card-video-bg') : null;
    }

    _stopVideo(card) {
      var v = this._getCardVideo(card);
      if (!v) return;
      try { v.pause(); v.currentTime = 0; } catch (e) {}
    }

    _playVideo(card) {
      var v = this._getCardVideo(card);
      if (!v) return;
      try {
        if (v.readyState < 2) v.load();
        var p = v.play();
        if (p && typeof p.catch === 'function') p.catch(function () {});
      } catch (e) {}
    }

    // ---- Active state ----

    _setActive(index) {
      if (index < 0 || index >= this.cards.length) return;

      var card = this.cards[index];
      var prevIndex = this._activeIndex;

      // Update dots + arrows
      this._updateDots(index);
      this._updateArrows(index);

      // Update card classes
      var self = this;
      this.cards.forEach(function (c) {
        c.classList.remove('is-active');
      });

      card.classList.add('is-active');
      this._activeIndex = index;

      // On mobile, debounce video play/stop to avoid thrashing during swipe
      if (!this.mq.matches) {
        if (this._videoDebounce) clearTimeout(this._videoDebounce);
        this._videoDebounce = setTimeout(function () {
          // Stop previous video
          if (self._activeCard && self._activeCard !== card) {
            self._stopVideo(self._activeCard);
          }
          self._activeCard = card;
          self._playVideo(card);
        }, 200);
      } else {
        // Desktop: immediate video swap
        if (this._activeCard && this._activeCard !== card) {
          this._stopVideo(this._activeCard);
        }
        this._activeCard = card;
        this._playVideo(card);
        this._slideToIndex(index);
      }
    }

    // ---- Desktop: transform-based carousel ----

    _setupDesktop() {
      // Let cards overflow the grid naturally — the parent (.dh-dd__cards)
      // has overflow:hidden and handles visual clipping.
      // The grid must NOT clip, otherwise transform can't reveal clipped cards.
      this.grid.style.overflowX = 'visible';
      this.grid.style.scrollSnapType = 'none';
    }

    _slideToIndex(index) {
      if (!this.grid || !this.cards.length) return;

      // Both firstCard and targetCard are shifted equally by any current transform,
      // so (targetLeft - firstLeft) always gives the natural spacing between them.
      // With row-reverse (flipped), card[0] is already at the right edge,
      // so the same logic positions the active card where card[0] was.
      var firstLeft = this.cards[0].getBoundingClientRect().left;
      var targetLeft = this.cards[index].getBoundingClientRect().left;
      var offset = targetLeft - firstLeft;

      this.grid.style.transition = 'transform 400ms cubic-bezier(0.25, 0.1, 0.25, 1)';
      this.grid.style.transform = 'translateX(' + (-offset) + 'px)';
    }

    // ---- Dot navigation ----

    _updateDots(activeIndex) {
      if (activeIndex === this._activeDotIndex) return;
      this._activeDotIndex = activeIndex;
      this.dots.forEach(function (dot, i) {
        dot.classList.toggle('is-active', i === activeIndex);
        dot.setAttribute('aria-current', i === activeIndex ? 'true' : 'false');
      });
    }

    _setupDots() {
      if (!this.dots.length) return;
      var self = this;
      this._dotHandlers = [];

      this.dots.forEach(function (dot, i) {
        var onClick = function () {
          if (self.mq.matches) {
            // Desktop: transform slide
            self._setActive(i);
          } else {
            // Mobile: scroll to card
            self._scrollToCard(i);
            self._setActive(i);
          }
        };
        dot.addEventListener('click', onClick);
        self._dotHandlers.push({ el: dot, handler: onClick });
      });
    }

    _teardownDots() {
      if (!this._dotHandlers) return;
      this._dotHandlers.forEach(function (h) {
        try { h.el.removeEventListener('click', h.handler); } catch (e) {}
      });
      this._dotHandlers = null;
    }

    // ---- Arrow navigation ----

    _setupArrows() {
      var self = this;
      this._arrowHandlers = [];

      if (this.arrowPrev) {
        var onPrev = function () {
          if (self._activeIndex > 0) {
            var idx = self._activeIndex - 1;
            if (!self.mq.matches) self._scrollToCard(idx);
            self._setActive(idx);
          }
        };
        this.arrowPrev.addEventListener('click', onPrev);
        this._arrowHandlers.push({ el: this.arrowPrev, handler: onPrev });
      }

      if (this.arrowNext) {
        var onNext = function () {
          if (self._activeIndex < self.cards.length - 1) {
            var idx = self._activeIndex + 1;
            if (!self.mq.matches) self._scrollToCard(idx);
            self._setActive(idx);
          }
        };
        this.arrowNext.addEventListener('click', onNext);
        this._arrowHandlers.push({ el: this.arrowNext, handler: onNext });
      }
    }

    _teardownArrows() {
      if (!this._arrowHandlers) return;
      this._arrowHandlers.forEach(function (h) {
        try { h.el.removeEventListener('click', h.handler); } catch (e) {}
      });
      this._arrowHandlers = null;
    }

    _updateArrows(index) {
      if (this.arrowPrev) this.arrowPrev.disabled = (index <= 0);
      if (this.arrowNext) this.arrowNext.disabled = (index >= this.cards.length - 1);
    }

    // ---- Mobile: native scroll ----

    _scrollToCard(index) {
      var card = this.cards[index];
      if (!card || !this.grid) return;

      var gridRect = this.grid.getBoundingClientRect();
      var cardRect = card.getBoundingClientRect();
      var scrollLeft = this.grid.scrollLeft + (cardRect.left - gridRect.left);

      this.grid.scrollTo({
        left: Math.max(0, scrollLeft),
        behavior: 'smooth'
      });
    }

    _setupMobileScroll() {
      // Ensure native scroll is enabled
      this.grid.style.overflowX = '';
      this.grid.style.scrollSnapType = '';
      this.grid.style.transform = '';
      this.grid.style.transition = '';

      var self = this;
      this._scrollTimer = null;

      this._onScroll = function () {
        if (self._scrollTimer) cancelAnimationFrame(self._scrollTimer);
        self._scrollTimer = requestAnimationFrame(function () {
          self._detectActiveFromScroll();
        });
      };

      this.grid.addEventListener('scroll', this._onScroll, { passive: true });
    }

    _detectActiveFromScroll() {
      if (!this.grid || !this.cards.length) return;

      var gridLeft = this.grid.getBoundingClientRect().left;
      var gridPadding = parseFloat(getComputedStyle(this.grid).paddingLeft) || 0;
      var snapTarget = gridLeft + gridPadding;
      var closest = 0;
      var closestDist = Infinity;

      for (var i = 0; i < this.cards.length; i++) {
        var cardLeft = this.cards[i].getBoundingClientRect().left;
        var dist = Math.abs(cardLeft - snapTarget);
        if (dist < closestDist) {
          closestDist = dist;
          closest = i;
        }
      }

      if (closest !== this._activeIndex) {
        this._setActive(closest);
      }
    }
  }

  customElements.define('dh-product-deep-dive', DhProductDeepDive);
}
